import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { CSSTransition } from 'react-transition-group';

type Positions = 'top' | 'right' | 'bottom' | 'left';

const POSITION: Record<Positions, string> = {
  top: 'translateY(-100%)',
  right: 'translateX(100%)',
  bottom: 'translateY(100%)',
  left: 'translateX(-100%)',
};

interface Props {
  active: boolean;
  offScreenPosition: Positions;
  transitionDuration: number;
  onExited: (element: HTMLElement) => void;
  dataTestId: string;
  children: ReactElement[];
}

const StyledPanel = styled.div<{
  $active: Props['active'];
  $offScreenPosition: Props['offScreenPosition'];
  $transitionDuration: Props['transitionDuration'];
}>`
  ${({ $active, $offScreenPosition, $transitionDuration }) => {
    const translate = $active ? 'translate(0px, 0px)' : POSITION[`${$offScreenPosition}`];

    return `
        background-color: var(--color-functional-panelLight);
        overflow: auto;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        transform: ${translate};
        transition: ${$transitionDuration}ms;
        visibility: ${$active ? 'visible' : 'hidden'};
        z-index: 1;
    `;
  }}
`;

function Panel(props: Props) {
  const { active, offScreenPosition, transitionDuration, onExited, dataTestId, children } = props;

  return (
    <CSSTransition in={active} timeout={transitionDuration} onExited={onExited}>
      <StyledPanel
        $active={active}
        $offScreenPosition={offScreenPosition}
        $transitionDuration={transitionDuration}
        data-testid={dataTestId}
      >
        {children}
      </StyledPanel>
    </CSSTransition>
  );
}

export default Panel;
